import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type Lang = "es" | "en";

interface BackupRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: string;
  tables: any;
  total_rows: number;
  total_bytes: number;
  storage_prefix: string | null;
  verification: any;
  error_message: string | null;
  triggered_by: string;
}

const fmtBytes = (n: number) => {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 2 : 1)} ${units[i]}`;
};

export function AdminBackupsPanel({ lang, toast }: { lang: Lang; toast: (m: string) => void }) {
  const [runs, setRuns] = useState<BackupRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const tr = (es: string, en: string) => (lang === "es" ? es : en);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from("backup_runs").select("*").order("started_at", { ascending: false }).limit(30);
    setRuns((data as any) || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    const { data, error } = await supabase.functions.invoke("daily-backup", { body: { triggeredBy: "manual" } });
    setRunning(false);
    if (error) { toast(tr("Error: ", "Error: ") + error.message); return; }
    toast(tr("Backup ejecutado", "Backup completed"));
    console.log("[daily-backup]", data);
    await load();
  };

  const download = async (runId: string, table: string) => {
    const { data, error } = await supabase.functions.invoke("backup-download", { body: { runId, table } });
    if (error || !data?.url) { toast(tr("Error al firmar URL", "Sign URL error")); return; }
    window.open(data.url, "_blank", "noopener");
  };

  const statusBadge = (s: string) => {
    const ok = s === "success";
    return (
      <span style={{
        background: ok ? "#16a34a22" : s === "running" ? "#3b82f622" : "#dc262622",
        color: ok ? "#16a34a" : s === "running" ? "#3b82f6" : "#dc2626",
        padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700,
      }}>{s}</span>
    );
  };

  if (loading) return <div style={{ padding: 30, color: "var(--tx3)" }}>{tr("Cargando…", "Loading…")}</div>;

  const lastSuccess = runs.find((r) => r.status === "success");
  const hoursSince = lastSuccess
    ? Math.round((Date.now() - new Date(lastSuccess.finished_at || lastSuccess.started_at).getTime()) / 36e5)
    : null;
  const fresh = hoursSince != null && hoursSince <= 26;

  return (
    <div className="ag">
      <div className="ag-hd">
        <h3>{tr("Backups diarios", "Daily backups")}</h3>
        <p>{tr(
          "Exporta tablas críticas a un bucket privado (JSON Lines) y verifica conteos. Corre todos los días a las 04:10 UTC.",
          "Exports critical tables to a private bucket (JSON Lines) and verifies counts. Runs daily at 04:10 UTC.",
        )}</p>
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 14, display: "grid", gap: 10 }}>
        <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{
            padding: "8px 14px", borderRadius: 8, fontWeight: 700,
            background: fresh ? "#16a34a22" : "#dc262622",
            color: fresh ? "#16a34a" : "#dc2626",
          }}>
            {lastSuccess
              ? (fresh ? tr("✅ Backup reciente", "✅ Recent backup") : tr("⚠️ Backup antiguo", "⚠️ Stale backup"))
              : tr("❌ Sin backups", "❌ No backups")}
            {hoursSince != null ? ` · ${hoursSince}h` : ""}
          </div>
          {lastSuccess && (
            <div style={{ color: "var(--tx3)", fontSize: 13 }}>
              {tr("Último éxito: ", "Last success: ")}
              {new Date(lastSuccess.finished_at || lastSuccess.started_at).toLocaleString()}
              {" · "}{lastSuccess.total_rows.toLocaleString()} {tr("filas", "rows")}
              {" · "}{fmtBytes(lastSuccess.total_bytes)}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn" onClick={runNow} disabled={running}>
            {running ? tr("Ejecutando…", "Running…") : tr("Ejecutar backup ahora", "Run backup now")}
          </button>
          <button className="btn alt" onClick={load}>{tr("Refrescar", "Refresh")}</button>
        </div>
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 14 }}>
        <h4 style={{ marginTop: 0 }}>{tr("Runbook de restore", "Restore runbook")}</h4>
        <p style={{ fontSize: 13, color: "var(--tx3)" }}>
          {tr(
            "Consulta docs/restore-runbook.md en el repo para los pasos completos. Resumen rápido:",
            "See docs/restore-runbook.md in the repo for full steps. Quick summary:",
          )}
        </p>
        <ol style={{ fontSize: 13, color: "var(--tx2)", lineHeight: 1.7 }}>
          <li>{tr("Confirma la fecha y prefijo del backup a restaurar.", "Confirm the date and prefix to restore.")}</li>
          <li>{tr("Descarga el .jsonl de la tabla afectada con el botón de cada fila.", "Download the .jsonl for the affected table from each row's button.")}</li>
          <li>{tr("En staging: importa con `COPY ... FROM STDIN` o un script que haga upsert por id.", "On staging: import with `COPY ... FROM STDIN` or an upsert-by-id script.")}</li>
          <li>{tr("Verifica conteos contra el campo verification del run.", "Verify counts against the run's verification field.")}</li>
          <li>{tr("Promueve a producción solo tras validar integridad referencial.", "Promote to production only after validating referential integrity.")}</li>
        </ol>
      </div>

      <div className="card" style={{ padding: 18 }}>
        <h4 style={{ marginTop: 0 }}>{tr("Historial", "History")}</h4>
        {runs.length === 0 ? (
          <p style={{ color: "var(--tx3)" }}>{tr("Sin backups todavía.", "No backups yet.")}</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--tx3)" }}>
                  <th style={th}>{tr("Inicio", "Started")}</th>
                  <th style={th}>{tr("Estado", "Status")}</th>
                  <th style={th}>{tr("Filas", "Rows")}</th>
                  <th style={th}>{tr("Tamaño", "Size")}</th>
                  <th style={th}>{tr("Origen", "Trigger")}</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <React.Fragment key={r.id}>
                    <tr style={{ borderTop: "1px solid var(--bd2)" }}>
                      <td style={td}>{new Date(r.started_at).toLocaleString()}</td>
                      <td style={td}>{statusBadge(r.status)}</td>
                      <td style={td}>{r.total_rows.toLocaleString()}</td>
                      <td style={td}>{fmtBytes(r.total_bytes)}</td>
                      <td style={td}>{r.triggered_by}</td>
                      <td style={td}>
                        <button className="btn alt" style={{ padding: "4px 10px", fontSize: 12 }}
                          onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                          {expanded === r.id ? tr("Ocultar", "Hide") : tr("Detalles", "Details")}
                        </button>
                      </td>
                    </tr>
                    {expanded === r.id && (
                      <tr><td colSpan={6} style={{ padding: "12px 8px 18px", background: "var(--bg2)" }}>
                        {r.error_message && (
                          <div style={{ color: "#dc2626", marginBottom: 10 }}>{r.error_message}</div>
                        )}
                        <div style={{ fontSize: 12, color: "var(--tx3)", marginBottom: 6 }}>
                          {tr("Prefijo: ", "Prefix: ")}<code>{r.storage_prefix}</code>
                        </div>
                        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                          <thead><tr style={{ color: "var(--tx3)" }}>
                            <th style={th}>{tr("Tabla", "Table")}</th>
                            <th style={th}>{tr("Exportadas", "Exported")}</th>
                            <th style={th}>{tr("Origen", "Source")}</th>
                            <th style={th}>{tr("Δ", "Δ")}</th>
                            <th style={th}>{tr("Tamaño", "Size")}</th>
                            <th style={th}></th>
                          </tr></thead>
                          <tbody>
                            {(Array.isArray(r.tables) ? r.tables : []).map((t: any) => {
                              const v = (Array.isArray(r.verification) ? r.verification : []).find((x: any) => x.table === t.table);
                              return (
                                <tr key={t.table} style={{ borderTop: "1px solid var(--bd2)" }}>
                                  <td style={td}>{t.table}</td>
                                  <td style={td}>{(t.exported ?? 0).toLocaleString()}</td>
                                  <td style={td}>{v ? (v.source ?? 0).toLocaleString() : "—"}</td>
                                  <td style={td}>{v ? (v.drift > 0 ? `+${v.drift}` : v.drift) : "—"}</td>
                                  <td style={td}>{fmtBytes(t.bytes ?? 0)}</td>
                                  <td style={td}>
                                    {t.ok && r.status === "success" && (
                                      <button className="btn alt" style={{ padding: "2px 8px", fontSize: 11 }}
                                        onClick={() => download(r.id, t.table)}>
                                        {tr("Descargar", "Download")}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const th: React.CSSProperties = { padding: "8px 6px", fontWeight: 600 };
const td: React.CSSProperties = { padding: "8px 6px", verticalAlign: "top" };